import type { PasskeyButtonChrome } from '@/components/auth/passkey-sign-in-button';
import type { CSSProperties } from 'react';

export const dottedBackground: CSSProperties = {
  backgroundImage:
    'radial-gradient(circle at center, color-mix(in srgb, var(--color-brand-700) 14%, transparent) 1.2px, transparent 1.2px)',
  backgroundSize: '32px 32px',
};

/**
 * The entry-flow canvas: the marketing site's flat paper in BOTH themes,
 * matching the native launch storyboard, the Capacitor splash background and
 * the pre-stylesheet critical CSS, so the storyboard -> splash -> webview
 * handoff is a single unbroken surface. Change one of those four and you must
 * change all four. Deliberately single-theme and deliberately FLAT: the site
 * it echoes carries no gradient, and a gradient here would not survive the
 * comparison with the flat native stages either side of it.
 */
export const brandCanvasClass = 'bg-entry-canvas text-entry-ink';

/**
 * Pins the semantic tokens the shared web auth chrome resolves, for the entry
 * canvas's subtree only.
 *
 * `brandCanvasClass` freezes the surface to cream in BOTH themes, but the auth
 * routes that render ON it (`reset-password`, `magic-link`, `update-password`,
 * `mfa-verify`, and `MFALoginView`) dress themselves from `AUTH_HEADING_CLASS`,
 * `AUTH_INPUT_CLASS`, `AUTH_LINK_CLASS` and `text-landing-fg/*`, every one of
 * which flips with the system theme. Unpinned, dark mode prints a near-white
 * heading (`--color-fg-primary` -> `#f0f6fc`) and a charcoal input box
 * (`--color-bg-tertiary` -> `#21262d`) on cream paper - on exactly the screens a
 * locked-out user reaches.
 *
 * Every value below is the token's own LIGHT-theme value, copied verbatim. That
 * is not an arbitrary palette choice: `--color-landing-bg` is `#f5f3ee` in light
 * mode, so this chrome was already authored against this cream, and pinning it
 * makes the dark-mode render identical to the light-mode one rather than a new
 * third look. Restyling those four routes onto the entry tokens proper is a
 * separate change; this closes the legibility defect without touching them.
 *
 * Keep in sync with `index.css`: a token added to the auth chrome that flips in
 * `.dark` has to be added here too, or it reappears as unreadable text on cream.
 */
export const entryCanvasTokenOverrides = {
  // Headings, body copy, labels, links (AUTH_HEADING/DESCRIPTION/LABEL/LINK).
  '--color-fg-primary': '#111322',
  '--color-fg-secondary': '#4a5578',
  '--color-fg-tertiary': '#7d89b0',
  '--color-fg-quaternary': '#b9c0d4',
  // Text inputs (AUTH_INPUT_CLASS), both its base and its `dark:` half.
  '--color-bg-primary': '#ffffff',
  '--color-bg-tertiary': '#f6f6f9',
  '--color-text-primary': '#111322',
  '--color-text-placeholder': '#b9c0d4',
  '--color-text-brand-primary': '#43389b',
  '--color-border-primary': '#dcdfea',
  '--color-border-brand': '#5e50da',
  '--primary': 'hsl(245 42% 26%)',
  // Submit buttons (AUTH_BUTTON_CLASS).
  '--color-brand-solid': '#43389b',
  '--color-brand-solid_hover': '#362d7c',
  // Raw `text-landing-fg/*` on the back links and the sent-state copy.
  '--color-landing-fg': '#141413',
  '--color-landing-bg': '#f5f3ee',
} as CSSProperties;

/**
 * The entry canvas's focus ring, in the one form worn by every control this
 * module styles.
 *
 * That is narrower than "every control on the canvas", and the gap is real:
 * `getAuthPageChrome()` forks native-vs-web only, so the desktop shell resolves
 * to the WEB chrome on the sibling auth routes. `AUTH_BUTTON_CLASS` carries no
 * focus outline of its own, so those submits fall back to
 * `base/buttons/button`'s default brand outline rather than this canvas's ink
 * ring — `/auth/magic-link`'s send-again and `/auth/reset-password`'s submits
 * today. Closing that means teaching the chrome map about desktop, not adding
 * a class here.
 *
 * `outline-solid` is load-bearing, not decorative. Several of these classes land
 * on `base/buttons/button` and `ui/input`, whose bases carry their own
 * `outline-*` utilities (a brand-coloured ring, or `focus-visible:outline-none`
 * which in Tailwind v4 is `outline-style: none` — a ring that never renders) and
 * whose `className` goes through tailwind-merge. Merge groups follow CSS
 * properties, so width, offset and colour sit in three groups that a style
 * utility is in none of - it survives the merge. `outline-solid` is in the SAME
 * group as `outline-none`, so it displaces it. On a plain `<button>` (no merged
 * base) it costs nothing: the width utilities already imply a solid style.
 *
 * Kept as one constant so the displacer cannot be present on some canvas
 * controls and missing on others. The two rings that differ - a tighter offset
 * for fields, brand purple for the submit pill - spell themselves out, and carry
 * `outline-solid` for the same reason.
 */
const ENTRY_FOCUS_RING =
  'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-entry-ink';

/**
 * Secondary sign-in method: an outlined pill on the sheet's paper. No fill -
 * the paper shows through, so the stack reads as one surface with a single
 * filled pill in it rather than as a list of cards.
 */
export const authOptionClass = `flex h-13 w-full items-center justify-center gap-3 rounded-full border border-entry-ink/15 px-5 text-md font-semibold text-entry-ink transition-[background-color,transform] hover:bg-entry-ink/5 motion-safe:active:scale-[0.985] motion-reduce:transition-colors ${ENTRY_FOCUS_RING} disabled:cursor-not-allowed disabled:opacity-60`;

/**
 * The one filled pill in the stack. Reserved for the primary method, and the
 * same ink pill the welcome screen's own primary action uses - the sheet is
 * the second half of that screen, not a different world.
 */
export const authPrimaryOptionClass = `flex h-13 w-full items-center justify-center gap-3 rounded-full bg-entry-ink px-5 text-md font-semibold text-entry-canvas transition-[background-color,transform] hover:bg-entry-ink/90 motion-safe:active:scale-[0.985] motion-reduce:transition-colors ${ENTRY_FOCUS_RING} disabled:cursor-not-allowed disabled:opacity-60`;

/**
 * The brand-purple submit pill: the entry canvas's answer to the web login's
 * `AUTH_BUTTON_CLASS`, and the ONE filled control in the desktop sign-in column.
 *
 * `brand-700` rather than the semantic `bg-brand-solid` the web button uses,
 * even though both resolve to `#43389b` in light mode: `--color-brand-solid` is
 * re-pointed inside `.dark`, and this canvas is the same cream in both themes.
 * The raw scale token is declared once and never re-declared, so the pill stays
 * the brand purple the web login shows.
 */
export const authBrandOptionClass =
  'flex h-13 w-full items-center justify-center gap-3 rounded-full bg-brand-700 px-5 text-md font-semibold text-white transition-colors hover:bg-brand-800 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700 disabled:cursor-not-allowed disabled:opacity-60';

/** Inline panel a chosen method expands into, below the pill stack. */
export const authPanelClass = 'rounded-2xl border border-entry-ink/12 bg-entry-ink/[0.03] p-4';

/**
 * Text inputs on the entry paper. The web auth field styles cannot be reused
 * here: they are built for a white card on a cool-grey page and carry a brand
 * focus ring, both of which read as a different product against this surface.
 * Shared by every entry-flow field (sign-in, magic link, contact form) so one
 * of them cannot quietly drift from the others.
 */
export const entryFieldClass =
  'h-12 w-full rounded-xl border border-entry-ink/15 bg-entry-canvas px-3.5 text-md text-entry-ink placeholder:text-entry-ink/35 focus-visible:border-entry-ink/40 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-entry-ink md:text-md';

/** The label above an `entryFieldClass` input. */
export const entryLabelClass = 'text-sm font-medium text-entry-ink/70';

/**
 * Quiet inline link on the entry paper. The web auth link cannot stand in: its
 * colour comes from the theme-flipping `--color-fg-tertiary`, and this canvas
 * is the same cream in both themes, so a dark theme would print it near-white
 * on paper.
 */
export const entryLinkClass = `h-auto p-0 text-sm font-medium text-entry-ink/60 underline underline-offset-2 transition-colors hover:text-entry-ink ${ENTRY_FOCUS_RING}`;

/**
 * The back affordance on the entry canvas, seated on the column's own left edge.
 *
 * Replaces `WEB_BACK_LINK_CLASS` outright on desktop rather than extending it,
 * so no class-order or tailwind-merge behaviour is load-bearing. Three things
 * differ from the web link, each for its own reason:
 *
 *  - `self-start`. The desktop sign-in column is a flex column, so its children
 *    stretch to the full column width. A back link is a shrink-to-fit
 *    `inline-flex` control that centres its own contents, so stretched it prints
 *    its label in the middle of the column while the heading and the fields
 *    below start at the left edge - one page, two left edges. This hands the
 *    control back its content width, so its arrow starts where the heading does.
 *  - `text-sm text-entry-ink/70`, the canvas's own secondary-text pairing (see
 *    `entryLabelClass`). The web link's `text-xs text-landing-fg/45` measures
 *    2.9:1 against the cream paper, under the 4.5:1 WCAG AA needs for text this
 *    size.
 *  - An explicit focus ring. `base/buttons/button`'s default is a brand-purple
 *    outline, which would otherwise leave this the only control on the canvas
 *    whose focus ring is not the canvas's own ink. The
 *    outline form matches `entryLinkClass` - canvas-local consistency; the
 *    design system's `ring-2` convention is reconciled canvas-wide separately.
 *    `ENTRY_FOCUS_RING` carries the `outline-solid` that makes it survive the
 *    merge with that base - see the constant.
 */
export const entryBackLinkClass = `self-start mb-4 h-auto p-0 text-sm font-medium text-entry-ink/70 hover:bg-transparent hover:text-entry-ink ${ENTRY_FOCUS_RING}`;

/**
 * Sign-in failure copy, rendered in place rather than over the sheet.
 *
 * Carries NO `empty:hidden`. The node is a live region that must stay mounted
 * AND in the accessibility tree while empty: `display: none` removes it from
 * that tree, so the first failure would arrive as a region the screen reader
 * has never seen and is not reliably announced. An empty node is zero-height,
 * and `not-empty:mb-4` keeps it free of the layout until it has something to
 * say.
 */
export const authInlineErrorClass = 'text-sm font-medium text-error-primary not-empty:mb-4';

/**
 * Seats the passkey affordance in the pill stack: the prominent form is a peer
 * of the other methods, the low-emphasis form a quiet line beneath them.
 */
export const passkeySheetChrome: PasskeyButtonChrome = {
  prominent: authOptionClass,
  // Flex, not block: while the ceremony pends a spinner joins the label, and
  // a block button leaves it hanging off the left edge instead of centred.
  subtle: `mx-auto flex items-center justify-center gap-2 text-sm font-medium text-entry-ink/60 underline underline-offset-2 transition-colors hover:text-entry-ink ${ENTRY_FOCUS_RING}`,
  container: '',
};
